/**
 * V2DefaultRedirect — when a user hits a legacy entry point, redirect
 * to the corresponding V2 surface. Was driven by a localStorage
 * kill-switch (`relo_v2_enabled`); K3 (2026-05-11) removed the
 * kill-switch so this is now unconditional once auth has resolved.
 *
 * The component picks a sensible target based on role:
 *   - admin       → '/v2'
 *   - client_user → '/v2/c/:clientSlug/portal'   (slug from route params)
 *   - partner     → '/v2/partner-portal/:partnerId'
 *
 * Callers can override with the `to` prop. When no target can be
 * computed (role has no V2 surface yet, or auth still loading) the
 * children are rendered so we don't flash a wrong-side route.
 */

import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface Props {
  children: React.ReactNode;
  /** Override the auto-computed target. */
  to?: string;
}

export function V2DefaultRedirect({ children, to }: Props) {
  const { isAdmin, isClientUser, isPartner, partnerId, loading } = useAuth();
  const params = useParams<{ clientSlug?: string }>();

  // Wait for auth to resolve so we don't flash legacy or misroute.
  if (loading) return <>{children}</>;

  let target = to;
  if (!target) {
    if (isAdmin) {
      target = '/v2';
    } else if (isClientUser && params.clientSlug) {
      target = `/v2/c/${params.clientSlug}/portal`;
    } else if (isPartner && partnerId) {
      target = `/v2/partner-portal/${partnerId}`;
    }
  }

  if (!target) return <>{children}</>;
  return <Navigate to={target} replace />;
}
